import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import styles from './MoneyFund.module.css';

/**
 * "קרן כספית" — create buy/sell instruction letters to the bank.
 * Approver picks Buy/Sell + amount; the backend produces the PDF, archives it,
 * and returns it for download. Below the form, a history of previous letters.
 */
export default function MoneyFundPage({ user }) {
  const [action, setAction] = useState('Buy');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const loadHistory = () => {
    setLoading(true);
    api.getMoneyFundRequests(user.credential)
      .then(setHistory)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadHistory(); }, [user.credential]); // eslint-disable-line

  const handleGenerate = async () => {
    const value = Number(amount);
    if (!value || value <= 0) {
      alert('נא להזין סכום תקין');
      return;
    }
    setSubmitting(true);
    try {
      const blob = await api.generateMoneyFundRequest(user.credential, action, value);
      const label = action === 'Buy' ? 'קנייה' : 'מכירה';
      const fileName = `קרן_כספית_${label}_${new Date().toLocaleDateString('he-IL').replace(/\//g, '-')}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      setAmount('');
      loadHistory();
    } catch (e) {
      alert('שגיאה בהפקת המכתב');
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const openPdf = async (id) => {
    setOpening(id);
    try {
      const { url } = await api.getMoneyFundRequestUrl(user.credential, id);
      window.open(url, '_blank');
    } catch (e) {
      alert('לא ניתן לפתוח את הקובץ');
    } finally {
      setOpening(null);
    }
  };

  const handleDelete = async (r) => {
    const label = actionLabel(r.actionType);
    if (!window.confirm(`למחוק את המכתב (${label}, ${formatAmount(r.amount)})?\nהפעולה אינה הפיכה.`)) return;
    setDeleting(r.id);
    try {
      await api.deleteMoneyFundRequest(user.credential, r.id);
      setHistory((prev) => prev.filter((x) => x.id !== r.id));
    } catch (e) {
      alert('שגיאה במחיקת המכתב');
      console.error(e);
    } finally {
      setDeleting(null);
    }
  };

  const formatAmount = (n) => `₪${Number(n).toLocaleString('he-IL', { minimumFractionDigits: 0 })}`;
  const formatDateTime = (iso) => new Date(iso).toLocaleString('he-IL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const actionLabel = (a) => (a === 'Buy' ? 'קנייה' : 'מכירה');

  return (
    <div className={styles.container}>
      <h2>קרן כספית</h2>
      <p className={styles.sub}>הפקת מכתב לבנק — קנייה או מכירה של יחידות בקרן הכספית</p>

      {/* Form */}
      <div className={styles.form}>
        <div className={styles.actionToggle}>
          <button
            type="button"
            className={`${styles.toggleBtn} ${action === 'Buy' ? styles.toggleActive : ''}`}
            onClick={() => setAction('Buy')}
          >
            קנייה
          </button>
          <button
            type="button"
            className={`${styles.toggleBtn} ${action === 'Sell' ? styles.toggleActive : ''}`}
            onClick={() => setAction('Sell')}
          >
            מכירה
          </button>
        </div>

        <label className={styles.label}>סכום (₪)</label>
        <input
          type="number"
          className={styles.input}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="לדוגמה: 250000"
          min="0"
        />

        <button className={styles.generateBtn} onClick={handleGenerate} disabled={submitting}>
          {submitting ? '⏳ מפיק...' : '📄 הפק מכתב לבנק'}
        </button>
      </div>

      {/* History */}
      <h3 className={styles.historyTitle}>מכתבים קודמים</h3>
      {loading && <p className={styles.empty}>טוען...</p>}
      {!loading && history.length === 0 && <p className={styles.empty}>עדיין לא הופקו מכתבים.</p>}

      {!loading && history.length > 0 && (
        <div className={styles.list}>
          {history.map((r) => (
            <div key={r.id} className={styles.card}>
              <div className={styles.cardMain}>
                <div className={styles.date}>
                  <span className={`${styles.badge} ${r.actionType === 'Buy' ? styles.badgeBuy : styles.badgeSell}`}>
                    {actionLabel(r.actionType)}
                  </span>
                  {formatDateTime(r.createdAt)}
                </div>
                <div className={styles.amount}>{formatAmount(r.amount)}</div>
              </div>
              <div className={styles.cardMeta}>
                {r.createdByName && <span>הופק ע"י {r.createdByName}</span>}
              </div>
              <div className={styles.cardActions}>
                {r.hasFile ? (
                  <button className={styles.viewBtn} onClick={() => openPdf(r.id)} disabled={opening === r.id}>
                    {opening === r.id ? 'פותח...' : '📄 צפה ב-PDF'}
                  </button>
                ) : (
                  <span className={styles.noFile}>אין קובץ שמור</span>
                )}
                <button
                  className={styles.deleteBtn}
                  onClick={() => handleDelete(r)}
                  disabled={deleting === r.id}
                >
                  {deleting === r.id ? 'מוחק...' : '🗑️ מחק'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
